import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, updateDoc, serverTimestamp, query, where, onSnapshot } from "firebase/firestore";
import { storage, ref, uploadBytes, getDownloadURL } from './firebaseUtils.js';
import Spinner from './Spinner.js';
import { ArrowLeft, Truck, User, MapPin, Clock, Paperclip, FileText, ExternalLink, Printer, CheckCircle, Upload, Loader2, FileCheck } from 'lucide-react';
import HorizontalTimeline from './components/HorizontalTimeline.js';
import { useConfig } from './hooks/useConfig.js';

const formatAddress = (addr) => {
  if (!addr) return '';
  return `${addr.address || ""}, ${addr.city || ""}, ${addr.state || ""} - ${addr.pincode || ""}`;
};

const InTransitDetail = ({ db }) => {
  const { config } = useConfig();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [order, setOrder] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [dcPreviewData, setDcPreviewData] = useState(null);
  const printRef = useRef();

  // Delivery State
  const [isDelivering, setIsDelivering] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState({ pod: false });
  const [deliveryData, setDeliveryData] = useState({
    deliveryDate: new Date().toISOString().split('T')[0],
    receiverName: '',
    receiverContact: '',
    podUrl: '',
    deliveryConfirmedBy: '',
    declarationChecked: false
  });

  const totals = useMemo(() => {
    if (!dcPreviewData || !dcPreviewData.components) return { totalQty: 0 };
    return dcPreviewData.components.reduce((acc, c) => {
      acc.totalQty += (parseInt(c.newQty || 0) + parseInt(c.rentalQty || 0));
      return acc;
    }, { totalQty: 0 });
  }, [dcPreviewData?.components]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const firmSnap = await getDocs(collection(db, "firms"));
        const fetchedFirms = firmSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setFirms(fetchedFirms);

        const docRef = doc(db, "dispatches", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const mData = docSnap.data();
          setFormData({
            id: docSnap.id,
            ...mData
          });

          // Reconstruct dcPreviewData similar to DCDetail.js
          const orderSnap = mData.orderId ? await getDoc(doc(db, "orders", mData.orderId)) : null;
          const orderData = orderSnap?.exists() ? { id: orderSnap.id, ...orderSnap.data() } : null;
          setOrder(orderData);

          const customerData = mData.customerID ? (await getDoc(doc(db, "customers", mData.customerID))).data() : null;

          const reconstructedComponents = (mData.components || []).map(mc => {
            let originalBaseQty = mc.perSet;
            if (!originalBaseQty && orderData) {
              orderData.boms?.forEach(bom => {
                const match = bom.components?.find(bc => bc.sku === mc.sku);
                if (match) originalBaseQty = match.baseQty;
              });
            }
            return { ...mc, perSet: originalBaseQty || 1, newQty: mc.newQty || 0, rentalQty: mc.rentalQty || 0 };
          });

          const newDcPreviewData = {
            firmId: mData.firmId || orderData?.firmId || '',
            challanNo: mData.challanNo || `DC-${mData.mocNo}`,
            dispatchDate: mData.dispatchDate || mData.deliveryDate || new Date().toISOString().split('T')[0],
            deliveryAgent: mData.deliveryAgent || '',
            deliveryMode: mData.deliveryMode || 'Truck',
            coordinator: mData.coordinator || mData.preparedBy || '',
            dispatchedThrough: mData.dispatchedThrough || '',
            poNumber: mData.poNumber || '',
            poDate: mData.poDate || '',
            irnNo: mData.irnNo || '',
            billToCustomer: mData.billToCustomer || customerData?.company_name || mData.customerName || '',
            shipToCustomer: mData.shipToCustomer || orderData?.shippingAddress?.attention || mData.customerName || '',
            billToAddress: mData.billToAddress || formatAddress(orderData?.billingAddress),
            shipToAddress: mData.shipToAddress || mData.shippingAddress || '',
            billToGstin: mData.billToGstin || customerData?.gstin || customerData?.gst_number || '',
            salesperson: mData.salesperson || orderData?.salesPerson || mData.approvedBySalesperson || '',
            orderBy: mData.orderBy || orderData?.billingAddress?.attention || '',
            orderByContact: mData.orderByContact || orderData?.billingAddress?.phone || '',
            sitePerson: mData.sitePerson || orderData?.shippingAddress?.attention || '',
            sitePersonContact: mData.sitePersonContact || orderData?.shippingAddress?.phone || '',
            billToEmail: mData.billToEmail || orderData?.billingAddress?.email || '',
            shipToEmail: mData.shipToEmail || orderData?.shippingAddress?.email || '',
            itemCode: mData.itemCode || '',
            towerHeight: mData.towerHeight || '',
            totalMeter: mData.totalMeter || mData.totalMeterNumber || '',
            receivedByName: mData.receivedByName || '',
            receivedByContact: mData.receivedByContact || '',
            handoverDateTime: mData.handoverDateTime || '',
            taxInvoiceUrl: mData.taxInvoiceUrl || '',
            ewayBillUrl: mData.ewayBillUrl || '',
            einvoiceUrl: mData.einvoiceUrl || '',
            isDCConfirmed: mData.isDCConfirmed || false,
            approvedByFinance: mData.approvedByFinance || '',
            components: reconstructedComponents
          };
          setDcPreviewData(newDcPreviewData);
          setSelectedFirm(fetchedFirms.find(f => f.id === newDcPreviewData.firmId));

          // Auto-fill delivery receiver details but keep them editable
          setDeliveryData(prev => ({
            ...prev,
            receiverName: newDcPreviewData.sitePerson || '',
            receiverContact: newDcPreviewData.sitePersonContact || ''
          }));
        } else {
          alert("Dispatch not found");
          navigate('/ops/in-transit');
        }
      } catch (error) {
        console.error("Error fetching In Transit Dispatch:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [db, id, navigate]);

  // Real-time subscription to dispatches for the timeline
  useEffect(() => {
    if (!formData?.orderId) return;
    const q = query(collection(db, "dispatches"), where("orderId", "==", formData.orderId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dispatchesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDispatches(dispatchesList);
    });
    return () => unsubscribe();
  }, [db, formData?.orderId]);

  const handleDeliveryChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDeliveryData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDocUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingDoc(prev => ({ ...prev, [type]: true }));
    try {
      const path = `delivery_docs/${id}/${type}_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setDeliveryData(prev => ({ ...prev, [`${type}Url`]: url }));
    } catch (err) {
      alert("Upload failed: " + err.message);
    } finally {
      setUploadingDoc(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleConfirmDelivery = async () => {
    if (!deliveryData.receiverName || !deliveryData.receiverContact || !deliveryData.deliveryConfirmedBy || !deliveryData.declarationChecked || !deliveryData.podUrl) {
        alert("Please fill all mandatory fields (Receiver details, Confirming Person, POD upload, and Check Declaration).");
        return;
    }

    setIsSaving(true);
    try {
      const timestamp = serverTimestamp();
      
      // Update Dispatch Document
      await updateDoc(doc(db, "dispatches", id), {
        dispatchStatus: 'delivered',
        deliveredAt: timestamp,
        deliveryDetails: { ...deliveryData },
        updatedAt: timestamp
      });

      // Update associated Order Document if it exists
      if (formData.orderId) {
        await updateDoc(doc(db, "orders", formData.orderId), {
            status: 'delivered',
            deliveryDetails: { ...deliveryData },
            updatedAt: timestamp
        });
      }

      alert("Delivery Confirmed Successfully!");
      navigate('/ops/in-transit'); // Or navigate to a Delivered List if it exists
    } catch (error) {
      alert("Error confirming delivery: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };


  const getFileNameFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const pathSegments = urlObj.pathname.split('/');
      return pathSegments[pathSegments.length - 1];
    } catch (error) {
      return url; // Return original URL if it's not a valid URL
    }
  };

  if (loading) return <Spinner />;
  if (!formData) return null; // Should not happen if loading is false and data is fetched or redirected

  const formatDate = (dateString) => {
    if (!dateString) return '---';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  const renderPreview = () => (
    <div style={pdfContainerStyle} ref={printRef}>
      {/* SECTION 1: HEADER */}
      <table style={headerTableStyle}>
        <tbody>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '20%', textAlign: 'center' }}>
              {selectedFirm?.logo && <img src={selectedFirm.logo} alt="Logo" style={{ maxWidth: '120px' }} />}
            </td>
            <td style={{ ...headerTableCellStyle, width: '50%', fontSize: '12px' }}>
              <strong style={{ fontSize: '16px' }}>{selectedFirm?.name}</strong><br />
              {selectedFirm?.address}<br />
              GSTIN: {selectedFirm?.gstin}<br />
              Email: {selectedFirm?.email} | Tel: {selectedFirm?.tel}
            </td>
            <td style={{ ...headerTableCellStyle, width: '30%', textAlign: 'center', verticalAlign: 'middle' }}>
              <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '900' }}>DELIVERY CHALLAN</h1>
            </td>
          </tr>
        </tbody>
      </table>

      {/* SECTION 2: DOCUMENT & DELIVERY */}
      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold' }}>
            <td style={{ ...headerTableCellStyle, width: '50%' }}>Document Details</td>
            <td style={{ ...headerTableCellStyle, width: '50%' }}>Delivery Details</td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}><strong>Challan#:</strong> {dcPreviewData.challanNo}</td>
            <td style={headerTableCellStyle}><strong>PO Number:</strong> {dcPreviewData.poNumber}</td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}><strong>Dispatch Date:</strong> {dcPreviewData.dispatchDate}</td>
            <td style={headerTableCellStyle}><strong>PO Date:</strong> {dcPreviewData.poDate}</td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}><strong>Delivery Agent:</strong> {dcPreviewData.deliveryAgent}</td>
            <td style={headerTableCellStyle}><strong>Delivery Mode:</strong> {dcPreviewData.deliveryMode}</td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}><strong>Coordinator:</strong> {dcPreviewData.coordinator}</td>
            <td style={headerTableCellStyle}><strong>Dispatched Through:</strong> {dcPreviewData.dispatchedThrough}</td>
          </tr>
          {dcPreviewData.irnNo && (
            <tr><td colSpan="2" style={headerTableCellStyle}><strong>IRN#:</strong> {dcPreviewData.irnNo}</td></tr>
          )}
        </tbody>
      </table>

      {/* SECTION 3: BILL TO | SHIP TO */}
      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold' }}>
            <td style={{ ...headerTableCellStyle, width: '50%' }}>Bill to</td>
            <td style={{ ...headerTableCellStyle, width: '50%' }}>Ship to</td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}><strong>Customer:</strong> {dcPreviewData.billToCustomer}</td>
            <td style={headerTableCellStyle}><strong>Customer:</strong> {dcPreviewData.shipToCustomer}</td>
          </tr>
          <tr>
            <td style={{ ...headerTableCellStyle, height: '50px', verticalAlign: 'top' }}><strong>Address:</strong> {dcPreviewData.billToAddress}</td>
            <td style={{ ...headerTableCellStyle, height: '50px', verticalAlign: 'top' }}><strong>Address:</strong> {dcPreviewData.shipToAddress}</td>
          </tr>
          <tr><td style={headerTableCellStyle}><strong>GSTIN/UIN:</strong> {dcPreviewData.billToGstin}</td><td rowSpan={2} style={headerTableCellStyle}></td></tr>
          <tr><td style={headerTableCellStyle}><strong>Salesperson:</strong> {dcPreviewData.salesperson}</td></tr>
          <tr>
            <td style={headerTableCellStyle}><strong>Order By:</strong> {dcPreviewData.orderBy}<br/><strong>Contact:</strong> {dcPreviewData.orderByContact}</td>
            <td style={headerTableCellStyle}><strong>Site Person:</strong> {dcPreviewData.sitePerson}<br/><strong>Contact:</strong> {dcPreviewData.sitePersonContact}</td>
          </tr>
          <tr><td style={headerTableCellStyle}><strong>Email:</strong> {dcPreviewData.billToEmail}</td><td style={headerTableCellStyle}><strong>Email:</strong> {dcPreviewData.shipToEmail}</td></tr>
        </tbody>
      </table>

      {/* SECTION 4: ITEM SUMMARY */}
      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '33%' }}><strong>Item code:</strong> {dcPreviewData.itemCode}</td>
            <td style={{ ...headerTableCellStyle, width: '33%' }}><strong>Tower Height:</strong> {dcPreviewData.towerHeight}</td>
            <td style={{ ...headerTableCellStyle, width: '34%' }}><strong>Total Meter:</strong> {dcPreviewData.totalMeter}</td>
          </tr>
        </tbody>
      </table>

      {/* SECTION 5: MATERIAL TABLE */}
      <div style={{ marginTop: '20px' }}>
          <div style={{ backgroundColor: '#f1f5f9', padding: '8px 10px', border: '1px solid #000', borderBottom: 'none', fontWeight: 'bold', fontSize: '14px' }}>MATERIAL BREAKDOWN</div>
          <table style={headerTableStyle}>
              <thead>
                  <tr style={{ backgroundColor: '#f8fafc' }}><th style={pdfThStyle}>Sr. No.</th><th style={pdfThStyle}>SKU</th><th style={{...pdfThStyle, textAlign:'left'}}>Item</th><th style={pdfThStyle}>Qty Per Set</th><th style={pdfThStyle}>Total Qty</th></tr>
              </thead>
              <tbody>
                  {dcPreviewData.components.map((c, i) => (
                      <tr key={i}>
                          <td style={{...pdfTdStyle, textAlign:'center'}}>{i+1}</td><td style={{...pdfTdStyle, textAlign:'center'}}>{c.sku}</td><td style={pdfTdStyle}>{c.name}</td>
                          <td style={{...pdfTdStyle, textAlign:'center'}}>{c.perSet}</td><td style={{...pdfTdStyle, textAlign:'center', fontWeight:'bold'}}>{parseInt(c.newQty || 0) + parseInt(c.rentalQty || 0)}</td>
                      </tr>
                  ))}
                  <tr style={{ backgroundColor: '#f8fafc', fontWeight: 'bold' }}><td colSpan="4" style={{ ...pdfTdStyle, textAlign: 'right', paddingRight: '10px' }}>TOTAL QUANTITY:</td><td style={{ ...pdfTdStyle, textAlign: 'center' }}>{totals.totalQty}</td></tr>
              </tbody>
          </table>
      </div>
      
      {/* SECTION 6: TERMS & CONDITIONS */}
      <table style={{ ...headerTableStyle, borderTop: 'none', marginTop:'20px' }}>
        <tbody>
          <tr style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold' }}><td style={{ ...headerTableCellStyle, fontSize: '14px' }}>Terms and Conditions</td></tr>
          <tr>
            <td style={{ ...headerTableCellStyle, fontSize: '11px', lineHeight: '1.6' }}>
                1. Have received the material in good condition and satisfied with the quality of same.<br/>
                2. Have read the scaffold safety notes and also the instructions given by the Nexrise India team for proper use of the scaffold.<br/>
                3. Received and carefully read the scaffold safety notes for meticulous compliance at site.<br/>
                4. Have verified and cross checked the material quantity. Any post discrepancy or issue for material condition will not be acceted.
            </td>
          </tr>
        </tbody>
      </table>

      {/* SECTION 7: SIGNATURES */}
      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '50%' }}>
                <strong>Received By (Name):</strong> {dcPreviewData.receivedByName || '____________________'}<br/><br/>
                <strong>Contact Number:</strong> {dcPreviewData.receivedByContact || '____________________'}<br/><br/>
                <strong>Date & Time of Handover:</strong> {dcPreviewData.handoverDateTime || '____________________'}
            </td>
            <td style={{ ...headerTableCellStyle, width: '50%', textAlign: 'center', verticalAlign: 'bottom', height: '120px' }}>
                <strong>Customer seal and Signature</strong>
            </td>
          </tr>
        </tbody>
      </table>

      {/* SECTION 8: INTERNAL PURPOSE ONLY */}
      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold' }}>
            <td colSpan="5" style={{ ...headerTableCellStyle, textAlign: 'center' }}>Internal Purpose Only</td>
          </tr>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '20%', verticalAlign: 'center',}}><strong>Verified and Checked By:</strong></td>
            <td rowSpan="3" style={{ ...headerTableCellStyle, width: '20%' }}></td>
            <td rowSpan="3" style={{ ...headerTableCellStyle, width: '20%', textAlign: 'center', verticalAlign: 'bottom', paddingBottom: '15px' }}>
                <strong>Seal & Sign (Consignor)</strong>
            </td>
            <td rowSpan="3" style={{ ...headerTableCellStyle, width: '20%', textAlign: 'center', verticalAlign: 'top' }}>
                <strong>Prepared By:</strong><br/>
                <div style={{ height: '110px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img src="/Rajesh-Kumar-Sign.png" alt="Sign" style={{ maxHeight: '100px', objectFit: 'contain' }} />
                </div>
                Rajesh Kumar
            </td>
            <td rowSpan="3" style={{ ...headerTableCellStyle, width: '20%', textAlign: 'center', verticalAlign: 'top' }}>
                <strong>Approved By:</strong><br/>
                <div style={{ height: '110px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img src="/Ghansyam-Sign.png" alt="Sign" style={{ maxHeight: '100px', objectFit: 'contain' }} />
                </div>
                Ghanshyam Prajapt
            </td>
          </tr>
          <tr><td style={{ ...headerTableCellStyle, verticalAlign: 'center' }}><strong>Time & date:</strong></td></tr>
          <tr><td style={{ ...headerTableCellStyle, verticalAlign: 'center' }}><strong>Sign:</strong></td></tr>
        </tbody>
      </table>

      {(dcPreviewData.taxInvoiceUrl || dcPreviewData.ewayBillUrl || dcPreviewData.einvoiceUrl) && (
        <div style={{ marginTop: '10px', fontSize: '9px', color: '#64748b', borderTop: '1px solid #e2e8f0', paddingTop: '5px' }}>
            <strong>Attached Docs:</strong>
            {dcPreviewData.taxInvoiceUrl && <span style={{ marginLeft: '10px' }}>Tax Invoice Attached ✅</span>}
            {dcPreviewData.ewayBillUrl && <span style={{ marginLeft: '10px' }}>E-way Bill Attached ✅</span>}
            {dcPreviewData.einvoiceUrl && <span style={{ marginLeft: '10px' }}>E-Invoice Attached ✅</span>}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      <div style={toolbarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/ops/in-transit')} style={backBtnStyle}><ArrowLeft size={24} /></button>
          <h2 style={{ margin: 0 }}>In Transit: {formData.mocNo}</h2>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
            {!isDelivering && (
                <button onClick={() => setIsDelivering(true)} style={confirmBtnStyle}>
                    <CheckCircle size={18} /> Confirm Delivery
                </button>
            )}
            <button onClick={() => {
                const printContent = printRef.current.innerHTML;
                const printWindow = window.open('', '_blank');
                printWindow.document.write(`
                  <html>
                    <head>
                      <title>DC ${dcPreviewData?.challanNo}</title>
                      <style>
                        body { font-family: Arial, sans-serif; color: #000; margin: 0; padding: 0; }
                        table { width: 100%; border-collapse: collapse; margin-bottom: 0px; }
                        td, th { border: 1px solid #000 !important; padding: 8px; text-align: left; font-size: 12px; }
                        @page { margin: 1cm; }
                      </style>
                    </head>
                    <body>${printContent}</body>
                  </html>
                `);
                printWindow.document.close();
                printWindow.focus();
                setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
            }} style={secondaryBtnStyle}><Printer size={18} /> Export PDF</button>
        </div>
      </div>

      {isDelivering && (
        <div style={{ ...cardStyle, border: '2px solid #10b981', marginBottom: '20px', animation: 'fadeIn 0.3s' }}>
            <h3 style={{ ...sectionHeaderStyle, color: '#10b981' }}><CheckCircle size={20} /> Complete Delivery</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                    <label style={labelStyle}>Date of Delivery</label>
                    <input type="date" name="deliveryDate" value={deliveryData.deliveryDate} onChange={handleDeliveryChange} style={inputStyle} />
                </div>
                <div>
                    <label style={labelStyle}>Order No.</label>
                    <input type="text" value={formData.orderNumber || ''} readOnly style={{...inputStyle, backgroundColor: '#f8fafc', color: '#64748b'}} />
                </div>
                <div>
                    <label style={labelStyle}>DC No.</label>
                    <input type="text" value={formData.challanNo || ''} readOnly style={{...inputStyle, backgroundColor: '#f8fafc', color: '#64748b'}} />
                </div>
                <div>
                    <label style={labelStyle}>Customer Name</label>
                    <input type="text" value={formData.customerName || ''} readOnly style={{...inputStyle, backgroundColor: '#f8fafc', color: '#64748b'}} />
                </div>
                 <div>
                    <label style={labelStyle}>Billing Email</label>
                    <input type="text" value={dcPreviewData?.billToEmail || ''} readOnly style={{...inputStyle, backgroundColor: '#f8fafc', color: '#64748b'}} />
                </div>
                <div>
                    <label style={labelStyle}>Shipping Email</label>
                    <input type="text" value={dcPreviewData?.shipToEmail || ''} readOnly style={{...inputStyle, backgroundColor: '#f8fafc', color: '#64748b'}} />
                </div>
                <div>
                    <label style={labelStyle}>Receiver Name *</label>
                    <input type="text" name="receiverName" value={deliveryData.receiverName} onChange={handleDeliveryChange} style={inputStyle} required />
                </div>
                <div>
                    <label style={labelStyle}>Receiver Contact *</label>
                    <input type="text" name="receiverContact" value={deliveryData.receiverContact} onChange={handleDeliveryChange} style={inputStyle} required />
                </div>
                 <div>
                    <label style={labelStyle}>Delivery Confirmed By *</label>
                    <select name="deliveryConfirmedBy" value={deliveryData.deliveryConfirmedBy} onChange={handleDeliveryChange} style={inputStyle} required>
                        <option value="">Select Name...</option>
                        {config.delivery_confirmed_by.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                    <label style={labelStyle}>Upload POD (Signed DC) *</label>
                    <input type="file" id="upload-pod" style={{ display: 'none' }} onChange={(e) => handleDocUpload(e, 'pod')} />
                    <label htmlFor="upload-pod" style={{ ...inputStyle, height:'60px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', cursor:'pointer', gap:'4px', backgroundColor: deliveryData.podUrl ? '#f0fdf4' : '#fff' }}>
                        {uploadingDoc.pod ? <Loader2 className="animate-spin" size={18} /> : deliveryData.podUrl ? <FileCheck size={18} color="#10b981" /> : <Upload size={18} color="#64748b" />}
                        <span style={{fontSize:'11px', fontWeight:'bold'}}>{deliveryData.podUrl ? 'POD Uploaded' : 'Choose File'}</span>
                    </label>
                </div>
            </div>

            <div style={{ marginBottom: '20px', backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>
                    <input type="checkbox" name="declarationChecked" checked={deliveryData.declarationChecked} onChange={handleDeliveryChange} style={{ width: '18px', height: '18px' }} required />
                    I have cross checked and confirmed with clients for material delivery.
                </label>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={() => setIsDelivering(false)} style={secondaryBtnStyle}>Cancel</button>
                <button onClick={handleConfirmDelivery} disabled={isSaving} style={confirmBtnStyle}>
                    {isSaving ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle size={18} />}
                    Submit Delivery
                </button>
            </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Driver & Vehicle Info */}
        <div style={cardStyle}>
          <h3 style={sectionHeaderStyle}><Truck size={18} /> Driver & Vehicle Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px' }}>
            <div>
              <label style={labelStyle}>Vehicle Number</label>
              <p style={valueStyle}>{formData.vehicleNumber || '---'}</p>
            </div>
            <div>
              <label style={labelStyle}>Vehicle Type</label>
              <p style={valueStyle}>{formData.vehicleType || '---'}</p>
            </div>
            <div>
              <label style={labelStyle}>Driver Name</label>
              <p style={valueStyle}>{formData.driverName || '---'}</p>
            </div>
            <div>
              <label style={labelStyle}>Driver Contact</label>
              <p style={valueStyle}>{formData.driverContact || '---'}</p>
            </div>
          </div>
        </div>

        {/* Shipping Details */}
        <div style={cardStyle}>
          <h3 style={sectionHeaderStyle}><MapPin size={18} /> Shipping Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div>
              <label style={labelStyle}>Customer Name</label>
              <p style={valueStyle}>{formData.customerName || '---'}</p>
            </div>
            <div>
              <label style={labelStyle}>Shipping Address</label>
              <p style={valueStyle}>{formData.shippingAddress || '---'}</p>
            </div>
          </div>
        </div>

        {/* Dispatch Timings */}
        <div style={cardStyle}>
          <h3 style={sectionHeaderStyle}><Clock size={18} /> Dispatch Timings</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div>
              <label style={labelStyle}>Vehicle IN Time</label>
              <p style={valueStyle}>{formData.vehicleInTime || '---'}</p>
            </div>
            <div>
              <label style={labelStyle}>Vehicle OUT Time (Dispatch)</label>
              <p style={valueStyle}>{formData.vehicleOutTime || '---'} on {formatDate(formData.vehicleDate)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Render DC Preview from DCDetail logic */}
      <div style={{ marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
        <h3 style={{...sectionHeaderStyle, marginBottom: '20px'}}><FileText size={18} /> Delivery Challan (Document)</h3>
        {dcPreviewData ? renderPreview() : <Spinner />}
      </div>

      {/* Document Attachments */}
      <div style={{...cardStyle, marginTop: '20px', animation: 'fadeIn 0.3s'}}>
        <h3 style={{...sectionHeaderStyle, marginBottom: '20px'}}>
          <Paperclip size={20} color="#2563eb"/> Document Attachments
        </h3>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px'}}>
          {formData.taxInvoiceUrl && (
            <a href={formData.taxInvoiceUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
              <div style={attachmentIconStyle2('#c7d2fe', '#4f46e5')}><FileText size={20}/></div>
              <div style={{flex: 1, minWidth: 0}}>
                <div style={attachmentLabelStyle}>Tax Invoice</div>
                <div style={attachmentNameStyle}>{getFileNameFromUrl(formData.taxInvoiceUrl)}</div>
              </div>
              <ExternalLink size={18} color="#6b7280"/>
            </a>
          )}
          {formData.ewayBillUrl && (
            <a href={formData.ewayBillUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
              <div style={attachmentIconStyle2('#dcfce7', '#166534')}><FileText size={20}/></div>
              <div style={{flex: 1, minWidth: 0}}>
                <div style={attachmentLabelStyle}>E-way Bill</div>
                <div style={attachmentNameStyle}>{getFileNameFromUrl(formData.ewayBillUrl)}</div>
              </div>
              <ExternalLink size={18} color="#6b7280"/>
            </a>
          )}
          {formData.einvoiceUrl && (
            <a href={formData.einvoiceUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
              <div style={attachmentIconStyle2('#fef9c3', '#854d0e')}><FileText size={20}/></div>
              <div style={{flex: 1, minWidth: 0}}>
                <div style={attachmentLabelStyle}>E-Invoice</div>
                <div style={attachmentNameStyle}>{getFileNameFromUrl(formData.einvoiceUrl)}</div>
              </div>
              <ExternalLink size={18} color="#6b7280"/>
            </a>
          )}
          {formData.vehicleProofUrl && (
            <a href={formData.vehicleProofUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
              <div style={attachmentIconStyle2('#e0f2fe', '#0284c7')}><FileText size={20}/></div>
              <div style={{flex: 1, minWidth: 0}}>
                <div style={attachmentLabelStyle}>Vehicle Proof</div>
                <div style={attachmentNameStyle}>{getFileNameFromUrl(formData.vehicleProofUrl)}</div>
              </div>
              <ExternalLink size={18} color="#6b7280"/>
            </a>
          )}
          {formData.deliveryChallanUrl && (
            <a href={formData.deliveryChallanUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
              <div style={attachmentIconStyle2('#ede9fe', '#5b21b6')}><FileText size={20}/></div>
              <div style={{flex: 1, minWidth: 0}}>
                <div style={attachmentLabelStyle}>Delivery Challan</div>
                <div style={attachmentNameStyle}>{getFileNameFromUrl(formData.deliveryChallanUrl)}</div>
              </div>
              <ExternalLink size={18} color="#6b7280"/>
            </a>
          )}
          {formData.bomSignedUrl && (
            <a href={formData.bomSignedUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
              <div style={attachmentIconStyle2('#fee2e2', '#991b1b')}><FileText size={20}/></div>
              <div style={{flex: 1, minWidth: 0}}>
                <div style={attachmentLabelStyle}>BOM (Signed)</div>
                <div style={attachmentNameStyle}>{getFileNameFromUrl(formData.bomSignedUrl)}</div>
              </div>
              <ExternalLink size={18} color="#6b7280"/>
            </a>
          )}
        </div>
      </div>
      {/* FOOTER TIMELINE */}
      {!loading && formData && (
        <div style={{ 
          marginTop: '40px', 
          maxWidth: '1100px', 
          margin: '50px auto 20px auto',
          animation: 'fadeIn 0.5s ease-in-out'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: 'bold', 
            color: '#374151', 
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Clock size={18} color="#2563eb" /> Order Timeline
          </h3>
          <HorizontalTimeline orderData={order} dispatchesData={dispatches} />
        </div>
      )}
    </div>
  );
};

// --- STYLES ---
const toolbarStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', backgroundColor: 'white', padding: '15px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };
const btnStyle = { padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', border: 'none' };
const confirmBtnStyle = { ...btnStyle, backgroundColor: '#059669', color: 'white' };
const secondaryBtnStyle = { padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#374151' };

const cardStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const sectionHeaderStyle = { fontSize: '16px', fontWeight: 'bold', borderBottom: '1px solid #f3f4f6', paddingBottom: '10px', marginBottom: '15px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' };
const valueStyle = { fontSize: '14px', color: '#1e293b', fontWeight: '500', marginBottom: '10px' };

const attachmentCardStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '12px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  backgroundColor: '#f8fafc',
  textDecoration: 'none',
  color: '#374151',
  transition: 'all 0.2s ease-in-out',
};

const attachmentIconStyle2 = (bg, color) => ({
  padding: '8px',
  backgroundColor: bg,
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: color,
});

const attachmentLabelStyle = {
  fontSize: '11px',
  fontWeight: '600',
  color: '#64748b',
  textTransform: 'uppercase',
  marginBottom: '2px',
};

const attachmentNameStyle = {
  fontSize: '13px',
  fontWeight: 'bold',
  color: '#1e293b',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const pdfContainerStyle = { backgroundColor: 'white', padding: '40px', border: '1px solid #e2e8f0', minHeight: '1000px', color: '#000' };
const headerTableStyle = { width: '100%', borderCollapse: 'collapse', border: '1px solid #000' };
const headerTableCellStyle = { border: '1px solid #000', padding: '8px', fontSize: '12px', textAlign: 'left' };
const pdfThStyle = { border: '1px solid #000', padding: '6px', fontSize: '11px', textAlign: 'center', fontWeight: 'bold' };
const pdfTdStyle = { border: '1px solid #000', padding: '6px', fontSize: '11px', textAlign: 'left' };

export default InTransitDetail;
